import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Crown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Recent Projects", path: "/projects" },
  { label: "Property Investment", path: "/investment" },
  { label: "For Estate Agents", path: "/estate-agents" },
  { label: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Transparent/white nav is only legible over a genuinely dark full-bleed hero.
  // The estate-agents hero is light, so keep the nav solid there for legibility.
  const darkHero =
    location.pathname === "/" ||
    /^\/projects\/[^/]+$/.test(location.pathname);
  const solid = scrolled || !darkHero;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-white/95 backdrop-blur-md border-b border-emerald-900/10 shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
          <span className={`grid place-items-center w-10 h-10 rounded-md transition-colors ${solid ? "bg-emerald-900" : "bg-white/10 backdrop-blur"}`}>
            <Crown className="w-5 h-5" style={{ color: "#C59B27" }} />
          </span>
          <span className="flex flex-col leading-none">
            <span className={`font-serif text-lg font-semibold tracking-tight whitespace-nowrap ${solid ? "text-emerald-950" : "text-white"}`}>Emerald Crown</span>
            <span className={`text-[10px] font-mono uppercase tracking-[0.25em] whitespace-nowrap ${solid ? "text-brass" : "text-white/70"}`}>Property Investments</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium whitespace-nowrap transition-colors hover:text-brass ${
                  solid ? (isActive ? "text-emerald-900" : "text-slate-600") : (isActive ? "text-brass" : "text-white/85")
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href="https://emeraldcrown.pattinson.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-auction"
            className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors hover:text-brass ${solid ? "text-slate-600" : "text-white/85"}`}
          >
            Auction <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="hidden lg:block lg:ml-8">
          <Link to="/sell">
            <Button data-testid="nav-cta-sell" className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full px-6">
              Sell us your property
            </Button>
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className={`lg:hidden ${solid ? "text-emerald-950" : "text-white"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border-t border-emerald-900/10 px-4 py-4 space-y-1 shadow-lg">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `block py-2.5 text-base font-medium ${isActive ? "text-emerald-900" : "text-slate-700"}`}
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href="https://emeraldcrown.pattinson.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-link-auction"
            className="flex items-center gap-1.5 py-2.5 text-base font-medium text-slate-700"
          >
            Auction <ExternalLink className="w-4 h-4" />
          </a>
          <Link to="/sell" onClick={() => setOpen(false)}>
            <Button className="w-full mt-2 bg-emerald-900 text-white rounded-full">Sell us your property</Button>
          </Link>
        </div>
      )}
    </header>
  );
};
